import React, { useCallback, useEffect, useRef, useState } from 'react';
import BZPopover from 'BZPopover';
import LoginPopover from '../../LoginPopover';
import classnames from 'classnames';
import { getTimeToSessionStartInMs } from '../../sessionBroadcastUtils';
import { useTimeout } from '../../../../../utils/ReactHooks';
import {
    sendCustomClick
} from '../../../../../utils/SnowplowUtils';
import {
    buttonTypes,
    rateSessionSubCategory
} from '../../../../../constants/SnowplowConst';
import { loginClickOrigin } from '../../../../../constants/Enums';
import { MAX_TIMEOUT_MILLISECONDS } from '../../../../../constants/Numbers';

const SEND_STATE = {
    idle: 'idle',
    sending: 'sending',
    sent: 'sent',
    failed: 'failed'
};

const SENT_CONFIRMATION_MS = 1500;

function isSettled(state) {
    return state === SEND_STATE.sent || state === SEND_STATE.failed;
}

export default function SessionRate({
    event,
    rate,
    feedback,
    settings,
    me,
    eventUrls,
    session,
    showEmailLogin,
    sendRate,
    siteConf,
    setLoginV2OffsetTop,
    sessionViewType,
}) {

    const [rateSelected, setRateSelected] = useState(rate || 0);
    const [feedbackText, setFeedbackText] = useState(undefined);
    const [sendState, setSendState] = useState(SEND_STATE.idle);

    const closePopoverRef = useRef(null);
    const registerClosePopover = useCallback((closePopover) => {
        closePopoverRef.current = closePopover;
    }, []);

    function closePopoverAfterSuccess() {
        closePopoverRef.current?.();
    }

    function sendRateClicked(e) {
        if (sendState === SEND_STATE.sending) {
            return;
        }
        sendCustomClick(buttonTypes.rateSession, rateSessionSubCategory.submitRateFeedbackForm, e.currentTarget);
        setSendState(SEND_STATE.sending);
        sendRate({
            eventId: event.id,
            sessionId: session.id,
            rate: rateSelected,
            feedback: feedbackText
        }).then(() => {
            setSendState(SEND_STATE.sent);
        }).catch(() => {
            setSendState(SEND_STATE.failed);
        });
    }

    useTimeout(closePopoverAfterSuccess, sendState === SEND_STATE.sent ? SENT_CONFIRMATION_MS : null);

    function changeRateSelected(idx) {
        let rate = idx + 1;
        if (rate === rateSelected) {
            rate = 0;
        }
        setRateSelected(rate);
        if (sendState !== SEND_STATE.sending && isSettled(sendState)) {
            setSendState(SEND_STATE.idle);
        }
    }

    const eventTimeZone = event && event['time-zone-id'];
    const {startDate, startMinute} = session || {};
    const isVirtualSession = session?.enableVirtualSession;
    const isScheduledSession = !!startDate;
    const timeToScheduledSessionStart = isScheduledSession
        ? getTimeToSessionStartInMs({ startDate, startMinute, eventTimeZone })
        : 0;
    const twoMinBeforeScheduledSessionStart = timeToScheduledSessionStart > 0
        ? timeToScheduledSessionStart - (2*60*1000)
        : 0;
    const timeToSessionStart = isVirtualSession
        ? twoMinBeforeScheduledSessionStart
        : timeToScheduledSessionStart;
    const isRateAllowedBeforeSessionStart = !settings?.rateAfterSessionStart;
    const isSessionStarted = timeToSessionStart < 0;
    const isSessionSoon = timeToSessionStart <= MAX_TIMEOUT_MILLISECONDS;

    const [isRateEnabled, enableRate] = useState(isRateAllowedBeforeSessionStart || isSessionStarted);

    useTimeout(
        () => enableRate(true),
        settings?.allowRating && settings?.rateAfterSessionStart && isScheduledSession && isSessionSoon ? timeToSessionStart : null
    );

    if (!settings?.allowRating) {
        return null;
    }

    let hearts = [];
    let selectedHearts = [];

    for (let i = 0; i < (rate || 0); i++) {
        hearts.push(<HeartIcon cls={'location-icon small-hearts'} iconType={'AgendaAtt_love'} fontSize={13} />);
    }
    for (let i = 0; i < 5 - (rate || 0); i++) {
        hearts.push(<HeartIcon cls={'location-icon small-hearts'} iconType={'AgendaAtt_heart'} fontSize={13} />);
    }

    for (let i = 0; i < rateSelected; i++) {
        selectedHearts.push(<HeartIcon cls={'location-icon'} iconType={'AgendaAtt_love'} fontSize={18} />);
    }
    for (let i = 0; i < 5 - rateSelected; i++) {
        selectedHearts.push(<HeartIcon cls={'location-icon'} iconType={'AgendaAtt_heart'} fontSize={18} />);
    }

    const rateClickHandler = (e, category) =>
        sendCustomClick(buttonTypes.rateSession, category, e.currentTarget);

    const button = (
        <div className="hearts-container" onClick={(e) => rateClickHandler(e, rateSessionSubCategory.clickRateOnPage)}>
            <span className="rate-title custom-color color-1 font-size-13">Rate Session</span>
            {
                hearts.map((heart, idx) => (
                    <span className='heart_wrapper' key={`heart-${idx}`}>{heart}</span>
                ))
            }
        </div>);

    if (!me.id) {
        return <BZPopover
            placement="bottom"
            cls="need-login-popover"
            button={button}
            popover={
                <LoginPopover
                    eventUrls={eventUrls}
                    onLoginClick={showEmailLogin}
                    setLoginV2OffsetTop={setLoginV2OffsetTop}
                    isWidget={siteConf?.isWidget}
                    analyticData={{
                        clickOrigin: sessionViewType === 'sessionPreviewWindow'
                            ? loginClickOrigin.sessionPreview.rateHeart
                            : loginClickOrigin.session.rateHeart,
                        sessionId: session?.id,
                    }}
                />
            }
        />;
    }

    const heartContainerCls = classnames('hearts-container pull-right', {disabled: !isRateEnabled});
    const isSending = sendState === SEND_STATE.sending;
    const sendButtonCls = classnames(
        'btn custom-color color-1-background text-color-white font-size-12 send-button',
        { disabled: isSending }
    );

    return <BZPopover
        placement="bottom"
        cls="agenda-rate-popover"
        button={button}
        popover={
            <SessionRatePopoverContent
                onClosePopoverReady={registerClosePopover}
                isRateEnabled={isRateEnabled}
                heartContainerCls={heartContainerCls}
                selectedHearts={selectedHearts}
                changeRateSelected={changeRateSelected}
                rateClickHandler={rateClickHandler}
                rate={rate}
                feedback={feedback}
                feedbackText={feedbackText}
                setFeedbackText={setFeedbackText}
                setRateSelected={setRateSelected}
                sendState={sendState}
                setSendState={setSendState}
                isSending={isSending}
                sendButtonCls={sendButtonCls}
                sendRateClicked={sendRateClicked}
            />
        }
    />;
}

function SessionRatePopoverContent({
    hidePopover,
    onClosePopoverReady,
    isRateEnabled,
    heartContainerCls,
    selectedHearts,
    changeRateSelected,
    rateClickHandler,
    rate,
    feedback,
    feedbackText,
    setFeedbackText,
    setRateSelected,
    sendState,
    setSendState,
    isSending,
    sendButtonCls,
    sendRateClicked,
}) {
    // BZPopover clones its `popover` element with a freshly-bound `hidePopover`
    // on every render, so keeping it in a ref (rather than the dep array) is
    // what actually keeps `closePopover`'s identity stable across renders.
    const hidePopoverRef = useRef(hidePopover);
    hidePopoverRef.current = hidePopover;

    const closePopover = useCallback(() => {
        hidePopoverRef.current();
        setSendState((currentState) => {
            if (isSettled(currentState)) {
                return SEND_STATE.idle;
            }
            return currentState;
        });
    }, []);

    useEffect(() => {
        onClosePopoverReady(closePopover);
    }, [closePopover, onClosePopoverReady]);

    return (
        <div>
            <div className="text text-left rate-wrapper">
                <span className="text-semi-bold">RATE THIS SESSION (1-5)</span>
                <span className={heartContainerCls}>
                    {selectedHearts.map((heart, idx) => (
                        <span key={`heart-${idx}`}
                            onClick={(e) => {
                                if (!isRateEnabled || isSending) {
                                    return;
                                }
                                changeRateSelected(idx);
                                rateClickHandler(e, rateSessionSubCategory.setActualSessionRate);
                            }}>{heart}</span>
                    ))}
                </span>
            </div>
            {!isRateEnabled && <div>
                <div className="text feedback-text  margin-top-small">
                    <span className="text-semi-bold">SESSION RATING AND FEEDBACK IS ONLY AVAILABLE ONCE SESSION STARTS</span>
                </div>
                <div className="margin-bottom-small text-center">
                    <a className="custom-color color-1 cancel-button"
                        onClick={closePopover}> CLOSE</a>
                </div>
            </div>}

            {isRateEnabled && <div>
                <div className="text feedback-text">
                    <span className="text-semi-bold">LEAVE FURTHER FEEDBACK</span>
                </div>
                <textarea value={feedbackText === undefined ? feedback : feedbackText}
                    className="form-control rate-popover"
                    aria-label="Leave further feedback"
                    disabled={isSending}
                    onChange={(e) => {
                        setFeedbackText(e.target.value);
                        if (isSettled(sendState)) {
                            setSendState(SEND_STATE.idle);
                        }
                    }}/>
                <span className="margin-vertical-small pull-right">
                    <a className={sendButtonCls}
                        onClick={isSending ? null : sendRateClicked}>
                        {isSending
                            ? <i className="fa fa-spinner fa-spin font-size-12"/>
                            : ' SEND '}
                    </a>
                </span>
                <span className="margin-vertical-small pull-left">
                    <a className="custom-color color-1 cancel-button"
                        onClick={isSending ? null : () => {
                            closePopover();
                            setRateSelected(rate);
                            setFeedbackText(feedback);
                        }
                        }> Cancel</a>
                </span>
                {sendState === SEND_STATE.sent &&
                    <p className="rate-feedback-message success-message color-1" role="status">
                        Thanks! Your feedback was sent.
                    </p>}
                {sendState === SEND_STATE.failed &&
                    <p className="rate-feedback-message error-message" role="alert">
                        We couldn&apos;t send your feedback. Please try again.
                    </p>}
            </div>}
        </div>
    );
}

const HeartIcon = ({ cls, iconType, fontSize }) => (
    <i className={`${cls} fa icon-${iconType} custom-color color-1 font-size-${fontSize}`}/>
);
